(ns basic-components.inputs.tesi-financials-2
  (:require
   [basic-components.inputs.footprint :refer [editor viewer display-string]]
   [common.localization :as loc]
   [reagent.core :as r]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [common.util :as cu]
   [common.currencies :as currencies]
   [cljs.reader :as edn]
   [reagent.dom :as rd]))

(def row-keys [:bookings :revenue :ebitda])

(def formula-fields
  [{:field-key   :revenue-growth
    :field-calc! (fn [data current-year all-years]
                   (if (= current-year (first all-years))
                     "n/a"
                     (let [previous-year (get all-years (dec (cu/index-of all-years current-year)))
                           my-revenue (get (:revenue data) current-year)
                           previous-revenue (get (:revenue data) previous-year)]
                       (if (or (not (number? my-revenue))
                               (not (number? previous-revenue)))
                         "#ERR"
                         (str (fu/format-with-separators (* 100.0 (- (/ my-revenue previous-revenue) 1)) 2 false 2)
                              "%")))))}
   {:field-key   :ebitda-ratio
    :field-calc! (fn [data current-year all-years]
                   (let [my-revenue (get (:revenue data) current-year)
                         my-ebitda (get (:ebitda data) current-year)]
                     (if (or (not (number? my-revenue))
                             (not (number? my-ebitda)))
                       "#ERR"
                       (str (fu/format-with-separators (* 100 (/ my-ebitda my-revenue)) 2 false 2)
                            "%"))))}])

(defn default-row
  [years]
  (reduce (fn [acc year]
            (assoc acc year nil))
          {}
          years))

(defn default-value
  []
  (let [d (js/Date.)
        current-year (.getFullYear d)
        years (range (- current-year 3) (+ current-year 6))]
    (reduce (fn [acc row-key]
              (assoc acc row-key (default-row years)))
            {:currency (:tenant/default-currency @session/tenant)}
            row-keys)))

(defn render
  [dom-atom args]
  (let [{:keys [:value :placeholder :class-name
                :has-focus?
                :on-focus! :on-blur! :on-change!
                :on-key-down! :disabled?]}
        args
        edit-value (or (:temp-value @dom-atom)
                       (when (map? value) value))
        render-value (or edit-value
                         (when (map? value) value)
                         (default-value))
        clear-state! (fn []
                       (swap! dom-atom dissoc :temp-value :decimals))
        years (->> (dissoc render-value :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort
                   (vec))
        currency (get render-value :currency)]
    [:table.oppo-financials
     [:thead
      [:tr.oppo-financials-header-row
       [:th.oppo-financials-cell.oppo-financials-cell--header
        [:select.oppo-financials-currency-selector
         {:ref       (fn [e]
                       (when (and e
                                  (not= e (:input-dom-node @dom-atom)))
                         (swap! dom-atom assoc :input-dom-node e)))
          :value     (if currency currency "")
          :on-change (fn [e]
                       (let [new-value (assoc render-value
                                              :currency
                                              (-> e
                                                  .-target
                                                  .-value
                                                  keyword))]
                         (swap! dom-atom assoc :temp-value new-value)
                         (when on-change!
                           (on-change! e new-value))
                         (when on-blur!
                           (on-blur! e new-value clear-state!))))}
         (fu/add-separator
          (doall (for [[k v] currencies/currencies]
                   ^{:key (str "financials-table-thead-currency-selector-" k)}
                   [:option
                    {:key   k
                     :value k}
                    v])))]]
       (doall
        (for [year years]
          ^{:key (str "financials-header-cell-" year)}
          [:th.oppo-financials-cell.oppo-financials-cell--header
           year
          ]))]]
     [:tbody
      (doall
       (for [row-key row-keys
             :let [row-data (get render-value row-key)]]
         ^{:key (str "financials-row-" row-key)}
         [:tr.oppo-financials-row
          [:td (loc/tr row-key)]
          (doall
           (for [year years
                 :let [cell-value (get row-data year)]]
             ^{:key (str "financials-cell-" row-key year)}
             [:td.oppo-financials-cell
              [:input
               {:placeholder (or placeholder "")
                :type        "text"
                :value       (let [draw-value cell-value
                                   min-decimal-count (if (nil? draw-value)
                                                       -1
                                                       (or (get-in @dom-atom [:decimals row-key year :decimal-count])
                                                           -1))
                                   edit-negative? (get-in @dom-atom [:decimals row-key year :negative?])]
                               (or (when draw-value
                                     (if (= draw-value "")
                                       (when edit-negative? "-")
                                       (fu/format-with-separators draw-value min-decimal-count edit-negative?)))
                                   ""))
                :on-change   (fn [e]
                               (let [input (-> e
                                               .-target
                                               .-value)
                                     float-value (fu/parse-float-info input "")
                                     new-cell-value (:float-value float-value)
                                     new-value (assoc-in render-value [row-key year] new-cell-value)]
                                 (swap! dom-atom assoc
                                   :temp-value
                                   new-value)
                                 (swap! dom-atom assoc-in
                                   [:decimals row-key year]
                                   {:decimal-count (or (:decimal-count float-value) -1)
                                    :negative?     (or (:negative? float-value) false)})
                                 (when on-change!
                                   (on-change! e new-value))))
                :on-blur     (fn [e]
                               (let [new-value (if (= cell-value "")
                                                 (assoc-in render-value [row-key year] nil)
                                                 render-value)]
                                 (swap! dom-atom assoc :temp-value new-value)
                                 (when on-blur!
                                   (on-blur! e new-value clear-state!))))
                :on-focus    (fn [e]
                             )
                :on-key-down on-key-down!}]]))]))]
     [:tbody
      (for [formula-field formula-fields]
        ^{:key (str "financials-row-" (:field-key formula-field))}
        [:tr.financials-summary-row
         [:td.financials-row
          [:span.calculated-row-label
           (loc/tr (:field-key formula-field))]]
         (doall (for [year years]
                  ^{:key (str "financials-calculated-cell-" (:field-key formula-field) "-" year)}
                  [:td.tesi-financials-cell.read-only
                   [:span.read-only-value
                    (when-let [calc! (:field-calc! formula-field)]
                      (calc! render-value year years))]]))])]]))

(defmethod editor :editor.type/tesi-financials-2
  [args]
  (let [dom-atom (or (:state-atom args) (r/atom nil))
        set-focusable! (:set-focusable-element! args)
        set-cancel-edit! (:set-cancel-edit! args)
        set-save! (:set-save! args)]
    (r/create-class
     {:display-name           "Custom financials"
      :component-did-mount    (fn [args]
                                (let []
                                  (when set-focusable!
                                    (set-focusable! (fn [_]
                                                      (when-let [dom-node (:input-dom-node @dom-atom)]
                                                        (.focus dom-node)))))
                                  (when set-cancel-edit!
                                    (set-cancel-edit! (fn [_]
                                                        (swap! dom-atom dissoc :temp-value)))) ;; save complete -> reset temp state
                                  (when set-save!
                                    (set-save! (fn [_]
                                                 (when-let [dom-node (:input-dom-node @dom-atom)]
                                                   (.blur dom-node)))))))
      :component-will-unmount (fn []
                                (swap! dom-atom dissoc :input-dom-node))
      :reagent-render         (fn [args]
                                (render dom-atom args))})))

(defn financials-array
  [args]
  (let [financials (:value args)
        financials (or financials (default-value))
        currency (->> (get financials :currency)
                      currencies/get-label)
        years (->> (dissoc financials :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort
                   vec)
        header (concat [currency] years)
        rows (for [row-key row-keys
                   :let [row-data (get financials row-key)]]
               (concat [(loc/tr row-key)]
                       (for [year years
                             :let [cell-value (get row-data year)]]
                         cell-value)))]
    (concat [header]
            rows)))

(defn calculated-array
  [args]
  (let [financials (:value args)
        financials (or financials (default-value))
        currency (->> (get financials :currency)
                      currencies/get-label)
        years (->> (dissoc financials :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort
                   vec)
        rows (for [formula-field formula-fields]
               (concat [(loc/tr (:field-key formula-field))]
                       (for [year years
                             :let [calc! (:field-calc! formula-field)]]
                         (when calc!
                           (calc! financials year years)))))]
    rows))

(defmethod display-string :editor.type/tesi-financials-2
  [args]
  (let [value (:value args)
        decoded (cond (string? value) (edn/read-string value)
                      (sequential? value) value
                      :else value)
        arr (financials-array (merge args
                                     {:value decoded}))
        calc-arr (calculated-array (merge args
                                          {:value decoded}))]
    (cu/tab-separated-string (concat arr calc-arr))))

(defn render-viewer
  [{:keys [value] :as args}]
  (let [render-value (or value
                         (when (map? value) value)
                         (default-value))
        years (->> (dissoc render-value :currency)
                   vals
                   (map keys)
                   (apply concat)
                   distinct
                   sort
                   (vec))
        currency (get render-value :currency)]
    [:div.preview.preview-financials
     [:table.oppo-financials
      [:thead
       [:tr.oppo-financials-header-row
        [:th.oppo-financials-cell.oppo-financials-cell--header
         currency]
        (doall
         (for [year years]
           ^{:key (str "financials-2-header-cell-" year)}
           [:th.oppo-financials-cell.oppo-financials-cell--header
            year]))]]
      [:tbody
       (doall
        (for [row-key row-keys
              :let [row-data (get render-value row-key)]]
          ^{:key (str "financials-2-row-" row-key)}
          [:tr.oppo-financials-row
           [:td (loc/tr row-key)]
           (doall
            (for [year years
                  :let [cell-value (get row-data year)]]
              ^{:key (str "financials-2-cell-" row-key year)}
              [:td.oppo-financials-cell
               cell-value]))]))]
      [:tbody
       (for [formula-field formula-fields]
         ^{:key (str "financials-2-row-" (:field-key formula-field))}
         [:tr.financials-summary-row
          [:td.financials-row
           [:span.calculated-row-label
            (loc/tr (:field-key formula-field))]]
          (doall
           (for [year years]
             ^{:key (str "financials-2-calculated-cell-" (:field-key formula-field) "-" year)}
             [:td.tesi-financials-cell.read-only
              [:span.read-only-value
               (when-let [calc! (:field-calc! formula-field)]
                 (calc! render-value year years))]]))])]]]))

(defmethod viewer :editor.type/tesi-financials-2
  [args]
  (r/create-class
   {:display-name   "tesi-financials-2/viewer"
    :reagent-render (fn [args]
                      (render-viewer args))}))
